import Service, { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import SessionService from './session';
import AlertsService from './alerts';
import { getGoogleAnalyticsClientID } from 'cio/utils/get-analytics-meta-properties';
import { GoogleOneClickMustChooseUserAccountError } from 'cio/authenticators/login-google-one-click';
import { TwoFactorAuthRequiredError } from 'cio/authenticators/login-2fa';
import { TwoFactorAuthLinkRequiredError } from 'cio/authenticators/login-2fa-link';
import ApiService, { ApiError } from 'cio/services/api';
import AuthService from './auth';
import { LoginSuccessResponse } from 'cio/authenticators/login-email-password';
import SsoConfiguration, { IdentityProvider } from 'cio/models/sso-configuration';
import OAuthService from './oauth';
import { NO_OAUTH_RESPONSE, Provider } from 'cio/utils/oauth';
import removeGoogleEmailAlias from 'cio/utils/remove-google-email-alias';
import AnalyticsService from './analytics';
import externalIdentityProviderLabel from 'cio/utils/external-identity-provider-label';

export enum LoginErrorType {
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  INVALID_EMAIL_PASSWORD = 'INVALID_EMAIL_PASSWORD',
  SSO_UNAUTHORIZED = 'SSO_UNAUTHORIZED',
  TWO_FACTOR_INVALID_CODE = 'TWO_FACTOR_INVALID_CODE',
  TWO_FACTOR_USED_CODE = 'TWO_FACTOR_USED_CODE',
  ATTEMPS_LIMIT_REACHED = 'ATTEMPS_LIMIT_REACHED',
  OAUTH_NO_RESPONSE = 'OAUTH_NO_RESPONSE',
  UNKNOWN = 'UNKNOWN',
}

export class LoginError extends Error {
  type: LoginErrorType;

  constructor(type: LoginErrorType, message: string) {
    super(message);
    this.type = type;
  }
}

export default class LoginService extends Service {
  @service session!: SessionService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service oauth!: OAuthService;
  @service analytics!: AnalyticsService;

  passwordReAuthenticate = dropTask(
    async ({ email, password }: { email: string; password: string }, twoFactorAuthEnabled: boolean | null) => {
      let reAuthenticateUrl = twoFactorAuthEnabled === true ? 'login_reauth' : 'login_email';
      let reAuthenticatePromise = this.api
        .post(reAuthenticateUrl, {
          data: { email, password, ga_client_id: this.getLoginMeta().ga_client_id },
          ignoreErrors: true,
          pathScope: 'global',
        })
        .then((res: LoginSuccessResponse) => {
          this.session.authenticate('authenticator:access-token', { accessToken: res.access_token });
          this.auth.setAccessCookiesAndExpiration(res.access_token, res.session_expires_at);
          this.analytics.track('user', 're_authenticated', { data: { method: 'password' } });
        })
        .catch((err) => {
          let errors: ApiError[] = (err.errors as ApiError[]) ?? [];
          if (errors.some((e) => e.status === '401')) {
            throw new LoginError(LoginErrorType.INVALID_PASSWORD, 'Invalid password');
          }
          throw err;
        });
      return this.loginTask.perform(reAuthenticatePromise);
    },
  );

  emailPassword = dropTask(async (email: string, password: string) => {
    let authenticatePromise = this.session
      .authenticate('authenticator:login-email-password', {
        email,
        password,
        meta: this.getLoginMeta(),
      })
      .catch((err) => {
        let errors: ApiError[] = (err.errors as ApiError[]) ?? [];
        if (errors.some((e) => e.status === '401')) {
          throw new LoginError(LoginErrorType.INVALID_EMAIL_PASSWORD, 'Invalid email or password');
        }
        throw err;
      });
    return this.loginTask.perform(authenticatePromise);
  });

  twoFactorAuthLink = dropTask(async (token: string) => {
    let authenticatePromise = this.session
      .authenticate('authenticator:login-2fa-link', {
        token,
        meta: this.getLoginMeta(),
      })
      .catch((err) => {
        let errors: ApiError[] = (err.errors as ApiError[]) ?? [];
        if (errors.some((e) => e.status === '401')) {
          throw new LoginError(LoginErrorType.INVALID_EMAIL_PASSWORD, 'Invalid authentication token');
        }
        throw err;
      });
    return this.loginTask.perform(authenticatePromise);
  });

  SSOGoogleOIDC = dropTask(
    async ({
      email,
      ssoConfigurationForLogin,
      userExternalIdentityEmail,
    }: {
      email: string;
      ssoConfigurationForLogin: SsoConfiguration;
      userExternalIdentityEmail: string;
    }) => {
      // for first Google SSO login, userExternalIdentityEmail will be empty:
      // pass user's e-mail as Google login_hint instead, which must match anyway for first login to work
      let loginHint = userExternalIdentityEmail || removeGoogleEmailAlias(email);

      let authenticatePromise = this.oauth
        .open(Provider.GoogleOIDC, {
          hostedDomain: ssoConfigurationForLogin.oidcGoogleHostedDomain,
          loginHint,
        })
        .then(({ authorizationCode, redirectUri }) =>
          this.session.authenticate('authenticator:login-sso', {
            email,
            authorizationCode,
            redirectUri,
            meta: this.getLoginMeta(),
          }),
        );

      return this.loginTask.perform(authenticatePromise, ssoConfigurationForLogin);
    },
  );

  SSOOktaOIDC = dropTask(
    async ({ email, ssoConfigurationForLogin }: { email: string; ssoConfigurationForLogin: SsoConfiguration }) => {
      let { authorizationURL } = await ssoConfigurationForLogin.getOpenIDConfigurationInfo();

      let authenticatePromise = this.oauth
        .open(Provider.OktaOIDC, {
          authorizationURL,
          clientID: ssoConfigurationForLogin.oidcClientId,
        })
        .then(({ authorizationCode, redirectUri }) =>
          this.session.authenticate('authenticator:login-sso', {
            email,
            authorizationCode,
            redirectUri,
            meta: this.getLoginMeta(),
          }),
        );
      return this.loginTask.perform(authenticatePromise, ssoConfigurationForLogin);
    },
  );

  SSOAzureOIDC = dropTask(
    async ({ email, ssoConfigurationForLogin }: { email: string; ssoConfigurationForLogin: SsoConfiguration }) => {
      let { authorizationURL } = await ssoConfigurationForLogin.getOpenIDConfigurationInfo();

      let authenticatePromise = this.oauth
        .open(Provider.AzureOIDC, {
          authorizationURL,
          clientID: ssoConfigurationForLogin.oidcClientId,
        })
        .then(({ authorizationCode, redirectUri }) =>
          this.session.authenticate('authenticator:login-sso', {
            email,
            authorizationCode,
            redirectUri,
            meta: this.getLoginMeta(),
          }),
        );
      return this.loginTask.perform(authenticatePromise, ssoConfigurationForLogin);
    },
  );

  SSOGenericOIDC = dropTask(
    async ({ email, ssoConfigurationForLogin }: { email: string; ssoConfigurationForLogin: SsoConfiguration }) => {
      let { authorizationURL } = await ssoConfigurationForLogin.getOpenIDConfigurationInfo();
      let authenticatePromise = this.oauth
        .open(Provider.GenericOIDC, {
          authorizationURL,
          clientID: ssoConfigurationForLogin.oidcClientId,
        })
        .then(({ authorizationCode, redirectUri }) =>
          this.session.authenticate('authenticator:login-sso', {
            email,
            authorizationCode,
            redirectUri,
            meta: this.getLoginMeta(),
          }),
        );
      return this.loginTask.perform(authenticatePromise, ssoConfigurationForLogin);
    },
  );

  googleOneClick = dropTask(async () => {
    this.analytics.track('user', 'sign_in_with_google_clicked');

    let authenticatePromise = this.oauth.open(Provider.GoogleOIDC).then(({ authorizationCode, redirectUri }) =>
      this.session.authenticate('authenticator:login-google-one-click', {
        authorizationCode,
        redirectUri,
        meta: this.getLoginMeta(),
      }),
    );

    return this.loginTask.perform(authenticatePromise);
  });

  googleOneClickChooseUserFinish = dropTask(
    async ({
      userID,
      googleOneClickChooseUserJWTToken,
    }: {
      userID: string;
      googleOneClickChooseUserJWTToken: string;
    }) => {
      let authenticatePromise = this.session.authenticate('authenticator:login-google-one-click-choose-user-finish', {
        userID,
        chooseUserJWTToken: googleOneClickChooseUserJWTToken,
        meta: this.getLoginMeta(),
      });

      return this.loginTask.perform(authenticatePromise);
    },
  );

  twoFactorAuth = dropTask(
    async ({
      twoFactorAuthCode,
      twoFactorAuthLoginToken,
    }: {
      twoFactorAuthCode: string;
      twoFactorAuthLoginToken: string;
    }) => {
      let authenticatePromise = this.session
        .authenticate('authenticator:login-2fa', {
          twoFactorAuthLoginToken,
          twoFactorAuthCode,
          meta: this.getLoginMeta(),
        })
        .catch((err) => {
          let errors = err.errors as ApiError[];
          if (errors.some((e) => e.detail === 'invalid authentication code')) {
            throw new LoginError(LoginErrorType.TWO_FACTOR_INVALID_CODE, 'Invalid authentication code');
          } else if (errors.some((e) => e.detail === 'already used this authentication code')) {
            throw new LoginError(LoginErrorType.TWO_FACTOR_USED_CODE, 'Already used this authentication code');
          }
          throw err;
        });

      return this.loginTask.perform(authenticatePromise);
    },
  );

  loginTask = dropTask(async (authenticatePromise: Promise<unknown>, ssoConfigurationForLogin?: SsoConfiguration) => {
    try {
      this.alerts.clearAlerts();
      return await authenticatePromise;
    } catch (err) {
      if (
        err instanceof GoogleOneClickMustChooseUserAccountError ||
        err instanceof TwoFactorAuthRequiredError ||
        err instanceof TwoFactorAuthLinkRequiredError ||
        err instanceof LoginError
      ) {
        throw err;
      } else {
        if (err.message === NO_OAUTH_RESPONSE) {
          throw new LoginError(
            LoginErrorType.OAUTH_NO_RESPONSE,
            this.getGenericLoginErrorMessage(ssoConfigurationForLogin),
          );
        }

        let errors = err.errors as ApiError[];
        if (errors.some((e) => e.status === '401')) {
          throw new LoginError(
            LoginErrorType.SSO_UNAUTHORIZED,
            this.getGenericLoginErrorMessage(ssoConfigurationForLogin),
          );
        }
        // General errors that can occur with any auth method
        if (errors.some((e) => e.detail === 'you have reached your attempts limit')) {
          throw new LoginError(
            LoginErrorType.ATTEMPS_LIMIT_REACHED,
            'Access completely denied as you have reached your attempts limit. Please contact win@customer.io for help logging into your account.',
          );
        } else {
          throw new LoginError(LoginErrorType.UNKNOWN, 'Problem logging in. Try again later.');
        }
      }
    }
  });

  private getGenericLoginErrorMessage(ssoConfigurationForLogin?: SsoConfiguration): string {
    // SSO or one-click login view: error authenticating with the external identity provider

    // if there's no SSO configuration, it means we're in Google One-Click login context:
    let externalIdentityProvider = ssoConfigurationForLogin?.identityProvider ?? IdentityProvider.GoogleOIDC;

    return `Something went wrong when authenticating with your ${externalIdentityProviderLabel(
      externalIdentityProvider,
    )} account, please try again.`;
  }

  private getLoginMeta() {
    return {
      ga_client_id: getGoogleAnalyticsClientID(),
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.

declare module '@ember/service' {
  interface Registry {
    login: LoginService;
  }
}
